import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import linkedCardService from '../../../services/linkedCardService';

const LinkedCardsManager = () => {
  const { userProfile } = useAuth();
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    cardName: '',
    cardNumber: '',
    cardHolderName: '',
    cardType: 'debit',
    expiryMonth: '',
    expiryYear: ''
  });
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (userProfile?.id) {
      loadCards();
    }
  }, [userProfile?.id]);

  const loadCards = async () => {
    try {
      setLoading(true);
      const data = await linkedCardService?.getAll(userProfile?.id);
      setCards(data);
      setError('');
    } catch (err) {
      setError(err?.message || 'Failed to load cards');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    
    // Format card number with spaces
    if (name === 'cardNumber') {
      const formatted = value?.replace(/\D/g, '')?.replace(/(\d{4})(?=\d)/g, '$1 ');
      setFormData(prev => ({ ...prev, [name]: formatted }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    
    setFormError('');
  };

  const validateForm = () => {
    if (!formData?.cardName || formData?.cardName?.trim()?.length < 2) {
      setFormError('Please enter a card name (minimum 2 characters)');
      return false;
    }
    
    if (!formData?.cardNumber || formData?.cardNumber?.replace(/\D/g, '')?.length < 13) {
      setFormError('Please enter a valid card number');
      return false;
    }
    
    if (!formData?.cardHolderName || formData?.cardHolderName?.trim()?.length < 3) {
      setFormError('Please enter cardholder name');
      return false;
    }
    
    if (!formData?.expiryMonth || !formData?.expiryYear) {
      setFormError('Please enter expiry date');
      return false;
    }
    
    const month = parseInt(formData?.expiryMonth);
    const year = parseInt(formData?.expiryYear);
    const currentYear = new Date()?.getFullYear();
    const currentMonth = new Date()?.getMonth() + 1;
    
    if (month < 1 || month > 12) {
      setFormError('Invalid expiry month');
      return false;
    }
    
    if (year < currentYear || (year === currentYear && month < currentMonth)) {
      setFormError('Card has expired');
      return false;
    }
    
    return true;
  };

  const handleAddCard = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;
    
    if (cards?.length >= 3) {
      setFormError('Maximum of 3 cards allowed. Please remove a card first.');
      return;
    }
    
    try {
      setSubmitting(true);
      setFormError('');
      
      await linkedCardService?.add({
        cardName: formData?.cardName,
        cardNumber: formData?.cardNumber,
        cardHolderName: formData?.cardHolderName,
        cardType: formData?.cardType,
        expiryMonth: parseInt(formData?.expiryMonth),
        expiryYear: parseInt(formData?.expiryYear)
      });
      
      // Reset form and reload cards
      setFormData({
        cardName: '',
        cardNumber: '',
        cardHolderName: '',
        cardType: 'debit',
        expiryMonth: '',
        expiryYear: ''
      });
      setShowAddForm(false);
      await loadCards();
    } catch (err) {
      setFormError(err?.message || 'Failed to add card');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSetActive = async (cardId) => {
    try {
      await linkedCardService?.setActive(cardId, userProfile?.id);
      await loadCards();
    } catch (err) {
      setError(err?.message || 'Failed to set active card');
    }
  };

  const handleDeleteCard = async (cardId) => {
    if (!window.confirm('Are you sure you want to remove this card?')) {
      return;
    }
    
    try {
      await linkedCardService?.delete(cardId, userProfile?.id);
      await loadCards();
    } catch (err) {
      setError(err?.message || 'Failed to delete card');
    }
  };

  const getCardIcon = (brand) => {
    switch (brand?.toLowerCase()) {
      case 'visa':
        return 'CreditCard';
      case 'mastercard':
        return 'CreditCard';
      case 'american express':
        return 'CreditCard';
      default:
        return 'CreditCard';
    }
  };

  const getCardColor = (brand) => {
    switch (brand?.toLowerCase()) {
      case 'visa':
        return '#1A1F71';
      case 'mastercard':
        return '#EB001B';
      case 'american express':
        return '#006FCF';
      default:
        return '#8B5CF6';
    }
  };

  if (loading) {
    return (
      <div className="card">
        <div className="flex items-center justify-center py-8">
          <Icon name="Loader" size={24} className="animate-spin text-primary" />
          <span className="ml-2 text-muted-foreground">Loading cards...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header">
        <div>
          <h2 className="card-title">Linked Cards</h2>
          <p className="card-description">Manage your debit and credit cards (max 3)</p>
        </div>
        {cards?.length < 3 && (
          <Button
            onClick={() => setShowAddForm(!showAddForm)}
            iconName={showAddForm ? 'X' : 'Plus'}
            size="sm"
          >
            {showAddForm ? 'Cancel' : 'Add Card'}
          </Button>
        )}
      </div>
      <div className="card-content">
        {error && (
          <div className="mb-4 p-3 bg-error/10 border border-error/20 rounded-lg flex items-start gap-2">
            <Icon name="AlertCircle" size={16} className="text-error mt-0.5" />
            <p className="text-sm text-error">{error}</p>
          </div>
        )}

        {/* Add Card Form */}
        {showAddForm && (
          <div className="mb-6 p-4 bg-muted/30 rounded-lg border border-border">
            <h3 className="text-sm font-semibold text-foreground mb-4">Add New Card</h3>
            <form onSubmit={handleAddCard} className="space-y-4">
              <Input
                label="Card Name"
                name="cardName"
                value={formData?.cardName}
                onChange={handleInputChange}
                placeholder="e.g., Personal Visa, Business Card"
                required
              />
              
              <Input
                label="Card Number"
                name="cardNumber"
                value={formData?.cardNumber}
                onChange={handleInputChange}
                placeholder="1234 5678 9012 3456"
                maxLength="19"
                required
              />
              
              <Input
                label="Cardholder Name"
                name="cardHolderName"
                value={formData?.cardHolderName}
                onChange={handleInputChange}
                placeholder="John Doe"
                required
              />
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-foreground mb-2 block">
                    Card Type
                  </label>
                  <select
                    name="cardType"
                    value={formData?.cardType}
                    onChange={handleInputChange}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    <option value="debit">Debit</option>
                    <option value="credit">Credit</option>
                  </select>
                </div>
                
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    label="Month"
                    name="expiryMonth"
                    type="number"
                    value={formData?.expiryMonth}
                    onChange={handleInputChange}
                    placeholder="MM"
                    min="1"
                    max="12"
                    required
                  />
                  <Input
                    label="Year"
                    name="expiryYear"
                    type="number"
                    value={formData?.expiryYear}
                    onChange={handleInputChange}
                    placeholder="YYYY"
                    min={new Date()?.getFullYear()}
                    required
                  />
                </div>
              </div>

              {formError && (
                <div className="p-3 bg-error/10 border border-error/20 rounded-lg flex items-start gap-2">
                  <Icon name="AlertCircle" size={16} className="text-error mt-0.5" />
                  <p className="text-sm text-error">{formError}</p>
                </div>
              )}

              <div className="flex gap-2">
                <Button type="submit" loading={submitting} fullWidth>
                  Add Card
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowAddForm(false)}
                  disabled={submitting}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        )}

        {/* Cards List */}
        {cards?.length === 0 ? (
          <div className="text-center py-8">
            <Icon name="CreditCard" size={48} className="mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground mb-4">No cards added yet</p>
            <Button onClick={() => setShowAddForm(true)} iconName="Plus" size="sm">
              Add Your First Card
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {cards?.map((card) => (
              <div
                key={card?.id}
                className={`p-4 rounded-lg border-2 transition-all ${
                  card?.isActive
                    ? 'border-primary bg-primary/5' :'border-border bg-card hover:border-primary/50'
                }`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: `${getCardColor(card?.cardBrand)}15` }}
                    >
                      <Icon
                        name={getCardIcon(card?.cardBrand)}
                        size={20}
                        color={getCardColor(card?.cardBrand)}
                      />
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground">
                        {card?.cardName} •••• {card?.cardNumberLastFour}
                      </h4>
                      <p className="text-xs text-muted-foreground capitalize">
                        {card?.cardBrand} {card?.cardType}
                      </p>
                    </div>
                  </div>
                  
                  {card?.isActive && (
                    <div className="flex items-center gap-1 px-2 py-1 bg-success/10 rounded-lg">
                      <Icon name="CheckCircle" size={14} className="text-success" />
                      <span className="text-xs font-semibold text-success">Active</span>
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-foreground font-medium">
                      {card?.cardHolderName}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Expires {String(card?.expiryMonth)?.padStart(2, '0')}/{card?.expiryYear}
                    </p>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {!card?.isActive && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleSetActive(card?.id)}
                      >
                        Set Active
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="destructive"
                      iconName="Trash2"
                      onClick={() => handleDeleteCard(card?.id)}
                    />
                  </div>
                </div>

                {card?.verificationStatus === 'verified' && (
                  <div className="mt-3 pt-3 border-t border-border">
                    <div className="flex items-center gap-1 text-xs text-success">
                      <Icon name="ShieldCheck" size={12} />
                      <span>Verified using Luhn algorithm</span>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {cards?.length > 0 && (
          <div className="mt-4 p-3 bg-muted/50 rounded-lg">
            <div className="flex items-start gap-2">
              <Icon name="Info" size={16} className="text-muted-foreground mt-0.5" />
              <div className="text-xs text-muted-foreground">
                <p className="font-semibold mb-1">Card Management:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Maximum 3 cards allowed per account</li>
                  <li>Only one card can be active at a time</li>
                  <li>All cards are verified using Luhn algorithm</li>
                  <li>Active card is used for wallet transactions</li>
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LinkedCardsManager;